/**
 * useInactivityTimeout - HIPAA-compliant session inactivity timeout
 * Automatically logs out users after a configurable period of inactivity.
 * Shows a warning modal before timeout.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import authService from '../services/api/authService';

const WARNING_BEFORE_MS = 2 * 60 * 1000; // Show warning 2 minutes before timeout

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'mousemove',
];

// Throttle interval for activity events to avoid excessive timer resets
const THROTTLE_MS = 30_000; // 30 seconds

export interface UseInactivityTimeoutReturn {
  showWarning: boolean;
  remainingSeconds: number;
  dismissWarning: () => void;
}

export function useInactivityTimeout(timeoutMinutes: number = 15): UseInactivityTimeoutReturn {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const INACTIVITY_TIMEOUT_MS = timeoutMinutes * 60 * 1000;

  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timeoutRef.current = null;
    warningRef.current = null;
    countdownRef.current = null;
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    // Fire-and-forget server-side token revocation
    authService.logout(refreshToken ?? undefined).catch(() => {});
    clearAuth();
    navigate('/login', { replace: true });
  }, [clearAllTimers, clearAuth, refreshToken, navigate]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    // Warning timer: fires 2 minutes before logout
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(Math.ceil(WARNING_BEFORE_MS / 1000));

      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Logout timer
    timeoutRef.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT_MS);
  // INACTIVITY_TIMEOUT_MS is a derived value (timeoutMinutes * 60000) — must be
  // in deps so timers rebuild when the session config changes.
  }, [clearAllTimers, handleLogout, INACTIVITY_TIMEOUT_MS]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    // Throttle: only reset if enough time has passed since last reset
    if (now - lastActivityRef.current < THROTTLE_MS) return;
    lastActivityRef.current = now;
    startTimers();
  }, [startTimers]);

  const dismissWarning = useCallback(() => {
    // User interacted with warning — reset the timers
    lastActivityRef.current = Date.now();
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    startTimers();

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      clearAllTimers();
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
    };
  }, [isAuthenticated, startTimers, handleActivity, clearAllTimers]);

  return { showWarning, remainingSeconds, dismissWarning };
}
